import { useQuery } from "@tanstack/react-query";
import { Play, Youtube } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/Editorial";
import SocialSection from "@/components/SocialSection";

const isYt = (v) => v.platform === "youtube" || /youtu\.?be/.test(v.link || "");
const isTt = (v) => v.platform === "tiktok" || /tiktok/.test(v.link || "");

function YtCard({ v }) {
  return (
    <a href={v.link || "#"} target="_blank" rel="noreferrer" data-testid={`yt-video-${v.id}`} className="dl-card overflow-hidden group">
      <div className="relative aspect-video bg-[#141A2E]">
        {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover group-hover:opacity-85 transition-opacity" loading="lazy" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full bg-black/55 border border-[#C6A76A]/60 flex items-center justify-center text-[#D4B978] group-hover:scale-110 transition-transform"><Play size={20} /></span>
        </div>
        {v.duration && <span className="absolute bottom-2 right-2 bg-black/80 text-[#F3E9D2] text-xs px-1.5 py-0.5">{v.duration}</span>}
      </div>
      <div className="p-4">
        {v.category && <div className="dl-eyebrow" style={{ fontSize: "0.55rem" }}>{v.category}</div>}
        <div className="serif text-lg text-[#F3E9D2] mt-1 leading-tight group-hover:text-[#D4B978] transition-colors">{v.title}</div>
      </div>
    </a>
  );
}

function TtCard({ v }) {
  return (
    <a href={v.link || "#"} target="_blank" rel="noreferrer" data-testid={`tt-video-${v.id}`} className="dl-card overflow-hidden group">
      <div className="relative aspect-[9/16] bg-[#141A2E]">
        {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover group-hover:opacity-85 transition-opacity" loading="lazy" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-11 h-11 rounded-full bg-black/55 border border-[#C6A76A]/60 flex items-center justify-center text-[#D4B978] group-hover:scale-110 transition-transform"><Play size={18} /></span>
        </div>
      </div>
      <div className="p-3">
        <div className="text-sm text-[#F3E9D2]/90 leading-tight">{v.title}</div>
      </div>
    </a>
  );
}

export default function Reseaux() {
  const { data: videos = [] } = useQuery({ queryKey: ["videos"], queryFn: () => getList("videos") });
  const yt = videos.filter(isYt);
  const tt = videos.filter(isTt);

  useSeo({
    title: "Réseaux sociaux",
    description: "Retrouvez Dorothée Lancelot sur YouTube, TikTok, Instagram et Facebook : vidéos, contenus historiques, médiumnité, deuil et ésotérisme.",
    path: "/reseaux-sociaux",
  });

  return (
    <div>
      <PageHeader eyebrow="Communauté" title="Retrouvez Dorothée Lancelot sur les réseaux" intro="Vidéos, réflexions et contenus autour de l'ésotérisme, du deuil, de l'histoire et de la médiumnité — sur mes différents réseaux." image={IMG.candle} />

      <section className="dl-container py-14">
       
<div className="flex flex-wrap gap-4">
<a className="dl-btn" href="https://www.facebook.com/DorotheeMedium.fr/" target="_blank" rel="noreferrer">Facebook</a>
<a className="dl-btn" href="https://www.instagram.com/dorotheelancelot/" target="_blank" rel="noreferrer">Instagram</a>
<a className="dl-btn" href="https://www.tiktok.com/@dorotheelancelot?_r=1&_t=ZG-99sbI81HYw5" target="_blank" rel="noreferrer">TikTok</a>
<a className="dl-btn" href="https://youtube.com/@dorotheelancelot?si=P01YpxYdL1bL9RRK" target="_blank" rel="noreferrer">YouTube</a>
</div>
        {yt.length > 0 && (
          <div className="mt-20" data-testid="yt-section">
            <div className="flex items-center gap-2 mb-8">
              <Youtube size={18} className="text-[#C6A76A]" />
              <h2 className="serif text-3xl text-[#F3E9D2]">Sur ma chaîne YouTube</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {yt.slice(0, 6).map((v) => <YtCard key={v.id} v={v} />)}
            </div>
          </div>
        )}

        {tt.length > 0 && (
          <div className="mt-20" data-testid="tt-section">
            <h2 className="serif text-3xl text-[#F3E9D2] mb-8">Sur TikTok</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {tt.slice(0, 10).map((v) => <TtCard key={v.id} v={v} />)}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
